/**
 * Storage de solicitações de cadastro de fornecedores (guia, terapeuta, comércio).
 * Novos cadastros ficam pendentes até aprovação do admin.
 */

import type { PerfilSolicitacaoFornecedor } from '../../../shared/lib/solicitacaoFornecedorPerfil';

export type TipoFornecedor =
  | 'GUIA'
  | 'TERAPEUTA'
  | 'COMERCIO_RESTAURANTE'
  | 'GESTOR_ATRATIVO'
  | 'ANFITRIAO_HOSPEDAGEM';
export type StatusSolicitacao = 'pendente' | 'aprovado' | 'recusado';

export interface SolicitacaoFornecedor {
  id: string;
  nome: string;
  email: string;
  roles: TipoFornecedor[];
  provider: 'email' | 'google';
  /** Preenchido pelo app do guia antes do envio. */
  perfilSolicitacao?: PerfilSolicitacaoFornecedor;
  createdAt: string;
  status: StatusSolicitacao;
  /** Quando aprovado, id do Guia ou Agencia criado no admin */
  guiaId?: string;
  agenciaId?: string;
  /** Mensagem opcional ao reprovar */
  mensagemRecusa?: string;
}

const KEY = 'naviex-solicitacoes-fornecedor';

/** Mesmo nome no admin (GuiasPage) para atualizar “Parceiros” ao chegar nova solicitação. */
export const BROADCAST_SOLICITACOES_FORNECEDOR = 'naviex-solicitacoes-fornecedor-updates';

function notifySolicitacoesListeners(): void {
  try {
    const ch = new BroadcastChannel(BROADCAST_SOLICITACOES_FORNECEDOR);
    ch.postMessage({ type: 'updated' });
    ch.close();
  } catch {
    /* ignore */
  }
}

function load(): SolicitacaoFornecedor[] {
  try {
    const s = localStorage.getItem(KEY);
    return s ? (JSON.parse(s) as SolicitacaoFornecedor[]) : [];
  } catch {
    return [];
  }
}

function save(list: SolicitacaoFornecedor[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function getSolicitacoes(): SolicitacaoFornecedor[] {
  return load();
}

export function getSolicitacoesPendentes(): SolicitacaoFornecedor[] {
  return load().filter((s) => s.status === 'pendente');
}

export function getSolicitacaoById(id: string): SolicitacaoFornecedor | null {
  return load().find((s) => s.id === id) ?? null;
}

export function addSolicitacao(data: Omit<SolicitacaoFornecedor, 'id' | 'status' | 'createdAt'>): SolicitacaoFornecedor {
  const list = load();
  const emailNorm = (data.email || '').trim().toLowerCase();
  const roles = (Array.isArray(data.roles) ? data.roles : []) as TipoFornecedor[];
  const provider: 'email' | 'google' = data.provider === 'google' ? 'google' : 'email';
  const mergedFields = {
    nome: (data.nome || '').trim(),
    email: (data.email || '').trim(),
    roles,
    provider,
    perfilSolicitacao: data.perfilSolicitacao,
  };
  const i = list.findIndex((s) => s.email.trim().toLowerCase() === emailNorm && s.status === 'pendente');
  if (i >= 0) {
    const prev = list[i];
    list[i] = {
      ...prev,
      ...mergedFields,
      perfilSolicitacao: data.perfilSolicitacao ?? prev.perfilSolicitacao,
      id: prev.id,
      status: 'pendente',
      createdAt: prev.createdAt,
    };
    save(list);
    notifySolicitacoesListeners();
    return list[i];
  }
  const id = `sol-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const nova: SolicitacaoFornecedor = {
    ...mergedFields,
    id,
    status: 'pendente',
    createdAt: new Date().toISOString(),
  };
  list.push(nova);
  save(list);
  notifySolicitacoesListeners();
  return nova;
}

export function setSolicitacaoStatus(
  id: string,
  status: StatusSolicitacao,
  guiaId?: string,
  agenciaId?: string,
  mensagemRecusa?: string
): void {
  const list = load();
  const i = list.findIndex((s) => s.id === id);
  if (i < 0) return;
  list[i] = {
    ...list[i],
    status,
    guiaId: guiaId ?? list[i].guiaId,
    agenciaId: agenciaId ?? list[i].agenciaId,
    mensagemRecusa: mensagemRecusa ?? list[i].mensagemRecusa,
  };
  save(list);
  notifySolicitacoesListeners();
}

export function removeSolicitacao(id: string): void {
  save(load().filter((s) => s.id !== id));
}
